import React, { useState, useRef } from 'react';
import { Eraser, Pipette, Plus, Palette } from 'lucide-react';
import { useImageUpload } from './useImageUpload';
import { ColorPickerField } from '../colorpicker/ColorPickerModal';
import {
  ShellFrame, SelectStep, CropStep, UploadingStep, PreviewPane,
  ToolSection, RecropSection, OrientationSection, MetaSection, ShellFooter
} from './ImageUploadParts';
import {
  removeImageBackground, replaceImageColor, samplePixelColor, checkHasTransparency, rgbToHex
} from './logoPixelOps';

/**
 * LogoUploadModal — the transparency-preserving uploader for brand marks (org LOGOS and ICONS).
 *
 * Scoped to brand marks on purpose. Background removal and colour replace only make sense on
 * flat artwork, and the PNG output exists to preserve alpha; neither applies to a photograph.
 * Photo uploads get their own component rather than a mode flag here, because the two flows
 * differ in output format, crop behaviour and which editing steps are even meaningful.
 *
 * Pipeline (source, crop, transforms, preview, encode) comes from useImageUpload; the shared
 * chrome from ImageUploadParts. What lives here is the two pixel-editing steps in between.
 */

// Lossless with alpha. Module-level so its identity is stable across renders.
const LOGO_FORMAT = { type: 'image/png', extension: 'png', label: 'PNG' };

const LogoUploadModal = ({
  isOpen, onClose, onUpload,
  title = 'Upload Image',
  suggestedName = '',
  aspectRatio,
  outputSize,
  maxOutputHeight,
  maxOutputWidth,
  maxSizeMB = 5,
  allowedTypes = '.png,.jpg,.jpeg,.svg,.webp'
}) => {
  // Background removal
  const [bgRemovalTolerance, setBgRemovalTolerance] = useState(30);
  const [bgRemoved, setBgRemoved] = useState(false);
  const [hasTransparency, setHasTransparency] = useState(false);
  const [pickedColor, setPickedColor] = useState(null);
  const [pickingColor, setPickingColor] = useState(false);
  const [removingBg, setRemovingBg] = useState(false);
  const [preRemovalDataUrl, setPreRemovalDataUrl] = useState(null);
  const [showTolerance, setShowTolerance] = useState(false);

  // Color replace
  const [crSourceColor, setCrSourceColor] = useState(null);
  const [crTargetColor, setCrTargetColor] = useState('#000000');
  const [crTolerance, setCrTolerance] = useState(30);
  const [crPickingSource, setCrPickingSource] = useState(false);
  const [crShowTolerance, setCrShowTolerance] = useState(false);
  const [crApplied, setCrApplied] = useState(false);
  const [crApplying, setCrApplying] = useState(false);

  const previewImgRef = useRef(null);

  const resetEditState = () => {
    setBgRemovalTolerance(30); setBgRemoved(false); setHasTransparency(false);
    setPickedColor(null); setPickingColor(false); setRemovingBg(false);
    setPreRemovalDataUrl(null); setShowTolerance(false);
    setCrSourceColor(null); setCrTargetColor('#000000'); setCrTolerance(30);
    setCrPickingSource(false); setCrShowTolerance(false); setCrApplied(false); setCrApplying(false);
  };

  const u = useImageUpload({
    isOpen, onClose, onUpload, suggestedName,
    aspectRatio, outputSize, maxOutputHeight, maxOutputWidth, maxSizeMB, allowedTypes,
    outputFormat: LOGO_FORMAT,
    onReset: resetEditState,
    // Keep the pre-removal image so "Re-remove" and the pipette always sample the original
    // pixels rather than an already-knocked-out copy, and tell the user if it arrived transparent.
    onAfterCrop: async (dataUrl) => {
      setPreRemovalDataUrl(dataUrl);
      try {
        setHasTransparency(await checkHasTransparency(dataUrl));
      } catch {
        setHasTransparency(false);
      }
    },
  });

  const onRecrop = () => {
    setPreRemovalDataUrl(null);
    setBgRemoved(false); setPickedColor(null); setHasTransparency(false);
    setShowTolerance(false);
    setCrSourceColor(null); setCrApplied(false); setCrShowTolerance(false);
  };

  // Click on preview to pick a color (shared by bg removal and color replace)
  const handlePreviewClick = async (e) => {
    if (!pickingColor && !crPickingSource) return;
    const img = previewImgRef.current;
    if (!img) return;

    const rect = img.getBoundingClientRect();
    const relX = (e.clientX - rect.left) / rect.width;
    const relY = (e.clientY - rect.top) / rect.height;

    try {
      const src = preRemovalDataUrl || u.workingSrc;
      const color = await samplePixelColor(src, relX, relY);
      if (pickingColor) {
        setPickedColor(color);
        setPickingColor(false);
      } else if (crPickingSource) {
        setCrSourceColor(color);
        setCrPickingSource(false);
      }
    } catch {
      u.setError('Could not sample color');
      setPickingColor(false);
      setCrPickingSource(false);
    }
  };

  const handleRemoveBackground = async () => {
    if (!pickedColor) return;
    const src = preRemovalDataUrl || u.workingSrc;
    if (!src) return;
    setRemovingBg(true);
    try {
      const dataUrl = await removeImageBackground(src, pickedColor, bgRemovalTolerance);
      u.setCroppedDataUrl(dataUrl);
      setBgRemoved(true);
      setHasTransparency(true);
    } catch {
      u.setError('Background removal failed');
    } finally {
      setRemovingBg(false);
    }
  };

  const handleColorReplace = async () => {
    if (!crSourceColor) return;
    const src = u.workingSrc;
    if (!src) return;
    // Parse target hex to RGB
    const hex = crTargetColor.replace('#', '');
    const targetRgb = { r: parseInt(hex.substring(0, 2), 16), g: parseInt(hex.substring(2, 4), 16), b: parseInt(hex.substring(4, 6), 16) };
    setCrApplying(true);
    try {
      const dataUrl = await replaceImageColor(src, crSourceColor, targetRgb, crTolerance);
      u.setCroppedDataUrl(dataUrl);
      setCrApplied(true);
    } catch {
      u.setError('Color replace failed');
    } finally {
      setCrApplying(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ShellFrame title={title} onClose={onClose} error={u.error} footer={<ShellFooter u={u} onClose={onClose} />}>
      {u.step === 'select' && (
        <SelectStep
          u={u} maxSizeMB={maxSizeMB} allowedTypes={allowedTypes}
          specText={<>Image will be converted to <strong>PNG with transparency</strong></>}
        />
      )}

      {u.step === 'crop' && u.sourceDataUrl && (
        <CropStep u={u} aspectRatio={aspectRatio}
          hint={<>Drag the handles to crop, then click <strong>Apply Crop</strong> to continue</>} />
      )}

      {u.step === 'edit' && (
        <div className="stui-imgupload-edit">
          <PreviewPane
            u={u}
            opaque={false}
            picking={pickingColor || crPickingSource}
            pickHint={pickingColor ? 'Click on the background color you want to remove' : 'Click on the color you want to change'}
            imgRef={previewImgRef}
            onImageClick={handlePreviewClick}
          />

          <div className="stui-imgupload-toolbar">
            <RecropSection u={u} onRecrop={onRecrop} />

            {/* 2. Remove Background */}
            <ToolSection num={2} done={bgRemoved || hasTransparency} label="Remove Background">
              {hasTransparency && !bgRemoved && (
                <div className="stui-imgupload-transparency-note">
                  This image already has transparency. You can skip this step or remove additional background.
                </div>
              )}

              <div className="stui-imgupload-tool-row">
                <button
                  className={`stui-imgupload-tool-btn${pickingColor ? ' active' : ''}`}
                  onClick={() => setPickingColor(!pickingColor)}
                >
                  <Pipette size={12} />
                  {pickingColor ? 'Click image...' : 'Pick Color'}
                </button>
                {pickedColor && (
                  <span className="stui-imgupload-swatch" style={{ backgroundColor: rgbToHex(pickedColor) }} title={rgbToHex(pickedColor)} />
                )}
              </div>

              <div className="stui-imgupload-tool-row">
                <button
                  className="stui-imgupload-tool-btn"
                  onClick={handleRemoveBackground}
                  disabled={removingBg || !pickedColor}
                >
                  <Eraser size={12} />
                  {removingBg ? 'Removing...' : bgRemoved ? 'Re-remove' : 'Remove BG'}
                </button>
                <button
                  className="stui-imgupload-tool-btn square"
                  onClick={() => setShowTolerance(!showTolerance)}
                  title="Fine-tune edge tolerance"
                >
                  <Plus size={10} className={`stui-imgupload-plus${showTolerance ? ' open' : ''}`} />
                </button>
              </div>

              {showTolerance && (
                <div className="stui-imgupload-tolerance">
                  <div className="stui-imgupload-tolerance-desc">Edge tolerance — how aggressively similar colors are removed</div>
                  <div className="stui-imgupload-tolerance-row">
                    <span className="stui-imgupload-tolerance-end">Less</span>
                    <input type="range" min={5} max={80} value={bgRemovalTolerance} onChange={e => setBgRemovalTolerance(parseInt(e.target.value))} />
                    <span className="stui-imgupload-tolerance-end">More</span>
                  </div>
                </div>
              )}
            </ToolSection>

            {/* 3. Color Replace */}
            <ToolSection num={3} done={crApplied} label="Replace Color">
              <div className="stui-imgupload-tool-row">
                <button
                  className={`stui-imgupload-tool-btn${crPickingSource ? ' active' : ''}`}
                  onClick={() => { setCrPickingSource(!crPickingSource); setPickingColor(false); }}
                >
                  <Pipette size={12} />
                  {crPickingSource ? 'Click image...' : 'From'}
                </button>
                {crSourceColor && (
                  <span className="stui-imgupload-swatch" style={{ backgroundColor: rgbToHex(crSourceColor) }} title={rgbToHex(crSourceColor)} />
                )}
                <span className="stui-imgupload-arrow">→</span>
                {/* The pixel ops need a plain hex, so keep result.hex even when the picker returns a token. */}
                <ColorPickerField
                  value={crTargetColor}
                  onChange={(_, result) => setCrTargetColor(result.hex)}
                  allowAlpha={false}
                  title="Target color"
                />
              </div>

              <div className="stui-imgupload-tool-row">
                <button
                  className="stui-imgupload-tool-btn"
                  onClick={handleColorReplace}
                  disabled={crApplying || !crSourceColor}
                >
                  <Palette size={12} />
                  {crApplying ? 'Applying...' : crApplied ? 'Re-apply' : 'Replace'}
                </button>
                <button
                  className="stui-imgupload-tool-btn square"
                  onClick={() => setCrShowTolerance(!crShowTolerance)}
                  title="Fine-tune color matching"
                >
                  <Plus size={10} className={`stui-imgupload-plus${crShowTolerance ? ' open' : ''}`} />
                </button>
              </div>

              {crShowTolerance && (
                <div className="stui-imgupload-tolerance">
                  <div className="stui-imgupload-tolerance-desc">How closely colors must match to be replaced</div>
                  <div className="stui-imgupload-tolerance-row">
                    <span className="stui-imgupload-tolerance-end">Exact</span>
                    <input type="range" min={5} max={80} value={crTolerance} onChange={e => setCrTolerance(parseInt(e.target.value))} />
                    <span className="stui-imgupload-tolerance-end">Loose</span>
                  </div>
                </div>
              )}
            </ToolSection>

            <OrientationSection u={u} num={4} />
            <MetaSection u={u} maxSizeMB={maxSizeMB} formatLabel={LOGO_FORMAT.label} />
          </div>
        </div>
      )}

      {u.step === 'uploading' && <UploadingStep />}
    </ShellFrame>
  );
};

export default LogoUploadModal;
